// file: src/services/docServices.js
import axios from 'axios';
import request from './requestServices';
import {
  API_DOCUMENT_LIST_URL,
  API_DOCUMENT_DELETE_URL,
  API_DOCUMENT_RENAME_URL,
  API_DOCUMENT_RUN_URL,
  API_DOCUMENT_GET_URL,
  API_GET_PRESIGNED_URL,
  API_CONFIRM_UPLOAD_URL,
  NOTEBOOK_OPEN_URL,
  JUPYTERLAB_SERVER_INFO_URL,
  API_CREATE_VIRTUAL_DOC_URL,
  JUPYTERLAB_SERVER_URL_ENDPOINT,
  JUPYTERLAB_DISASSOCIATE_URL
} from './apiRoutes';

// Utility to extract server_name from a JupyterLab location string
function extractServerNameFromLocation(location) {
  if (!location) return undefined;
  // Match /v1/eks_jupyterlab/proxy/server-<server_name>
  const match = location.match(/\/v1\/eks_jupyterlab\/proxy\/(server-[a-zA-Z0-9\-]+)/);
  return match ? match[1] : undefined;
}

// Patch listDocs to always include server_name for jupyterlab files
export async function listDocs(kbId) {
  const url = `${API_DOCUMENT_LIST_URL}?kb_id=${kbId}`;
  try {
    const { data } = await request(url, { method: 'GET' });
    let docs = [];
    if (data.data?.docs) {
      docs = data.data.docs;
    } else if (Array.isArray(data.data)) {
      docs = data.data;
    }
    // Patch: for jupyterlab files, ensure server_name is present
    docs = docs.map(doc => {
      if (doc.type === 'jupyterlab' && !doc.server_name && doc.location) {
        return { ...doc, server_name: extractServerNameFromLocation(doc.location) };
    }
      return doc;
    });
    return docs;
  } catch (error) {
    // Safely convert error to string
    const errorMessage = typeof error === 'string' ? error : 
                        (error.toString ? error.toString() : 'Unknown error');
    console.error('Error listing documents:', errorMessage);
    return [];
  }
}

export async function deleteDoc(docId) {
  const payload = { doc_id: docId };
  try {
    const { data } = await request(API_DOCUMENT_DELETE_URL, {
    method: 'POST',
    data: payload,
    showSuccessMessage: true,
  });
    
    // Some endpoints might return true/false directly in data.data
    // or they might not have any nested data structure
    // Handle both cases safely
    return data.data !== undefined ? data.data : true;
  } catch (error) {
    // Safely convert error to string
    const errorMessage = typeof error === 'string' ? error : 
                        (error.toString ? error.toString() : 'Unknown error');
    console.error(`Error deleting document ${docId}:`, errorMessage);
    throw new Error(errorMessage);
  }
}

export async function renameDoc(docId, newName) {
  const payload = { doc_id: docId, name: newName };
  try {
    const { data } = await request(API_DOCUMENT_RENAME_URL, {
    method: 'POST',
    data: payload,
    showSuccessMessage: true,
  });
    return data.data !== undefined ? data.data : true;
  } catch (error) {
    // Safely convert error to string
    const errorMessage = typeof error === 'string' ? error : 
                        (error.toString ? error.toString() : 'Unknown error');
    console.error(`Error renaming document ${docId}:`, errorMessage);
    throw new Error(errorMessage);
  }
}

export async function runDocs(docIds, options = {}) {
  const payload = {
    doc_ids: docIds,
    run: '1',
    ...options,
  };
  try {
    const { data } = await request(API_DOCUMENT_RUN_URL, {
    method: 'POST',
    data: payload,
    showSuccessMessage: false,
  });
    return data.data !== undefined ? data.data : true;
  } catch (error) {
    // Safely convert error to string
    const errorMessage = typeof error === 'string' ? error : 
                        (error.toString ? error.toString() : 'Unknown error');
    console.error('Error running document processing:', errorMessage);
    throw new Error(errorMessage);
  }
}

export async function getDocProgress(docId, kbId, forceCompletionCheck = false) {
  /**
   * NOTE: This function should be used for real-time progress updates.
   * 
   * This function is used:
   * 1. For initial document state retrieval
   * 2. For progress tracking during document processing
   * 3. For final state verification after processing completes
   */
  if (!kbId) {
    throw new Error('KB ID is required for getting document progress');
  }
  
  try {
    const url = `${API_DOCUMENT_LIST_URL}?doc_id=${docId}&kb_id=${kbId}`;
    
    // For final completion checks, add a cache-busting parameter and force=true to bypass server caching
    const finalUrl = forceCompletionCheck 
      ? `${url}&_t=${Date.now()}_force&force=true` 
      : `${url}&_t=${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    
    const { data } = await request(finalUrl, {
      method: 'GET',
      showSuccessMessage: false,
    });
    
    if (data?.data?.docs?.length > 0) {
      // Find the requested document in the returned array instead of always taking the first one
      const requestedDoc = data.data.docs.find(doc => doc.id === docId);
      
      if (requestedDoc) {
        // Normalize progress - convert from string/various formats to float between 0 and 1
        let normalizedProgress;
        
        // Handle different types of progress values
        if (typeof requestedDoc.progress === 'string') {
          // Convert string to number
          normalizedProgress = parseFloat(requestedDoc.progress);
        } else if (typeof requestedDoc.progress === 'number') {
          // Already a number
          normalizedProgress = requestedDoc.progress;
        } else {
          // Default if undefined or null
          normalizedProgress = 0; 
        }
        
        // Handle NaN case
        if (isNaN(normalizedProgress)) {
          normalizedProgress = 0;
        }
        
        // Convert percentage format (0-100) to decimal format (0-1)
        if (normalizedProgress > 1) {
          normalizedProgress = normalizedProgress / 100;
        }
        
        // Handle run values - convert to string for consistent comparison
        const runValue = String(requestedDoc.run || "1");
        
        // Completed documents should have run=0
        const isCompleted = runValue === "0";
        
        // Force progress=1 if run=0 (completed)
        if (isCompleted && normalizedProgress !== 1) {
          normalizedProgress = 1;
        }
        
        // Round to 2 decimal places to avoid floating point precision issues
        normalizedProgress = Math.round(normalizedProgress * 100) / 100;
        
        return {
          ...requestedDoc,
          progress: normalizedProgress,
          run: runValue
        };
      } else {
        if (data.data.docs.length > 0) {
          return data.data.docs[0]; // Fallback to first doc for backward compatibility
        }
      }
    }
    return null;
  } catch (error) {
    console.error('Error fetching document progress:', error);
    return null;
  }
}

/**
 * Retrieve the raw file data (PDF, CSV, etc.) as an ArrayBuffer
 * using raw axios, bypassing any JSON parsing.
 */
export async function getDocFileData(docId) {
  const url = `${API_DOCUMENT_GET_URL}/${docId}`;
  try {
  // Use raw axios to avoid JSON parsing issues:
  const response = await axios.get(url, {
    responseType: 'arraybuffer',
    withCredentials: true, // if your server requires credentials
  });
  return response.data; // The ArrayBuffer
  } catch (error) {
    // Safely convert error to string
    const errorMessage = typeof error === 'string' ? error : 
                        (error.toString ? error.toString() : 'Unknown error');
    console.error(`Error fetching file data for document ${docId}:`, errorMessage);
    throw new Error(errorMessage);
  }
}



export async function getServerInfo() {
  try {
    const response = await axios.get(JUPYTERLAB_SERVER_INFO_URL, {
      withCredentials: true
    });
    return response.data;
  } catch (error) {
    // Safely convert error to string
    const errorMessage = typeof error === 'string' ? error : 
                        (error.toString ? error.toString() : 'Unknown error');
    console.error('Error getting JupyterLab server info:', errorMessage);
    throw new Error(errorMessage);
  }
}

/**
 * Get a pre-signed URL for direct uploading to Storage
 */
export async function getPresignedUrl(kbId, file) {
  const payload = {
    kb_id: kbId,
    filename: file.name,
    file_size: file.size,
    content_type: file.type
  };
  
  try {
    const { data } = await request(API_GET_PRESIGNED_URL, {
      method: 'POST',
      data: payload,
      showSuccessMessage: false
    });
    
    // Handle both response formats
    if (data.data) {
      return data.data;
    } else {
      return data;
    }
  } catch (error) {
    // Safely convert error to string
    const errorMessage = typeof error === 'string' ? error : 
                        (error.toString ? error.toString() : 'Unknown error');
    console.error(`Error getting presigned URL for KB ${kbId}:`, errorMessage);
    throw new Error(errorMessage);
  }
}

/**
 * Upload a file directly to Storage using a pre-signed URL
 */
export async function uploadToPresignedUrl(presignedUrl, file) {
  try {
    const response = await axios.put(presignedUrl, file, {
      headers: {
        'Content-Type': file.type
      },
      withCredentials: false // No need for cookies on direct storage requests
    });
    
    return response.status >= 200 && response.status < 300;
  } catch (error) {
    // Safely convert error to string
    const errorMessage = typeof error === 'string' ? error : 
                        (error.response?.data?.message || error.message || 'Upload failed');
    console.error('Error uploading to presigned URL:', errorMessage);
    throw new Error(errorMessage);
  }
}

/**
 * Confirm to the backend that a file has been uploaded to Storage
 */
export async function confirmUpload(docId, bucket, fileLocation) {
  const payload = {
    doc_id: docId,
    bucket: bucket,
    file_location: fileLocation
  };
  
  try {
    const { data } = await request(API_CONFIRM_UPLOAD_URL, {
      method: 'POST',
      data: payload,
      showSuccessMessage: true
    });
    
    return data.data !== undefined ? data.data : true;
  } catch (error) {
    // Safely convert error to string
    const errorMessage = typeof error === 'string' ? error : 
                        (error.toString ? error.toString() : 'Unknown error');
    console.error('Error confirming upload:', errorMessage);
    throw new Error(errorMessage);
  }
}

export async function createDoc(doc) {
  try {
    const { data } = await request(API_DOCUMENT_LIST_URL, {
      method: 'POST',
      data: doc,
    });
    
    // Handle different response formats
    if (data.data) {
      return data.data;
    } else {
      return data;
    }
  } catch (error) {
    // Safely convert error to string
    const errorMessage = typeof error === 'string' ? error : 
                        (error.toString ? error.toString() : 'Unknown error');
    console.error('Error creating document:', errorMessage);
    throw new Error(errorMessage);
  }
}

export async function createBlenderVirtualDoc(serverName, kbId) {
  try {
    const { data } = await request('/v1/eks_blender/create_virtual_doc', {
      method: 'POST',
      data: {
        server_name: serverName,
        kb_id: kbId
      },
      showSuccessMessage: true,
    });
    return data.data;
  } catch (error) {
    const errorMessage = typeof error === 'string' ? error : 
                        (error.toString ? error.toString() : 'Unknown error');
    console.error('Error creating virtual document for Blender server:', errorMessage);
    throw new Error(errorMessage);
  }
}

export async function createFreeCADVirtualDoc(serverName, kbId) {
  try {
    const { data } = await request('/v1/eks_freecad/create_virtual_doc', {
      method: 'POST',
      data: {
        server_name: serverName,
        kb_id: kbId
      },
      showSuccessMessage: true,
    });
    return data.data;
  } catch (error) {
    const errorMessage = typeof error === 'string' ? error : 
                        (error.toString ? error.toString() : 'Unknown error');
    console.error('Error creating virtual document for FreeCAD server:', errorMessage);
    throw new Error(errorMessage);
  }
}

export async function createJupyterLabVirtualDoc(serverName, kbId) {
  try {
    const { data } = await request(API_CREATE_VIRTUAL_DOC_URL, {
      method: 'POST',
      data: {
        server_name: serverName,
        kb_id: kbId
      },
      showSuccessMessage: true,
    });
    return data.data;
  } catch (error) {
    const errorMessage = typeof error === 'string' ? error : 
                        (error.toString ? error.toString() : 'Unknown error');
    console.error('Error creating virtual document for JupyterLab server:', errorMessage);
    throw new Error(errorMessage);
  }
}

export async function createKiCadVirtualDoc(serverName, kbId) {
  try {
    const { data } = await request('/v1/eks_kicad/create_virtual_doc', {
      method: 'POST',
      data: {
        server_name: serverName,
        kb_id: kbId
      },
      showSuccessMessage: true,
    });
    return data.data;
  } catch (error) {
    const errorMessage = typeof error === 'string' ? error : 
                        (error.toString ? error.toString() : 'Unknown error');
    console.error('Error creating virtual document for KiCad server:', errorMessage);
    throw new Error(errorMessage);
  }
}

export async function getBlenderUrl(serverName) {
  try {
    const { data } = await request(`/v1/eks_blender/url/${serverName}`, {
      method: 'GET',
    });
    return data;
  } catch (error) {
    const errorMessage = typeof error === 'string' ? error : (error.toString ? error.toString() : 'Unknown error');
    console.error('Error fetching Blender URL:', errorMessage);
    throw new Error(errorMessage);
  }
}

export async function getFreeCADUrl(serverName) {
  try {
    const { data } = await request(`/v1/eks_freecad/url/${serverName}`, {
      method: 'GET',
    });
    return data;
  } catch (error) {
    const errorMessage = typeof error === 'string' ? error : (error.toString ? error.toString() : 'Unknown error');
    console.error('Error fetching FreeCAD URL:', errorMessage);
    throw new Error(errorMessage);
  }
}

export async function getJupyterLabUrl(serverName) {
  try {
    const { data } = await request(JUPYTERLAB_SERVER_URL_ENDPOINT(serverName), {
      method: 'GET',
    });
    return data;
  } catch (error) {
    const errorMessage = typeof error === 'string' ? error : (error.toString ? error.toString() : 'Unknown error');
    console.error('Error fetching JupyterLab labUrl:', errorMessage);
    throw new Error(errorMessage);
  }
}

export async function getKiCadUrl(serverName) {
  try {
    const { data } = await request(`/v1/eks_kicad/url/${serverName}`, {
      method: 'GET',
    });
    return data;
  } catch (error) {
    const errorMessage = typeof error === 'string' ? error : (error.toString ? error.toString() : 'Unknown error');
    console.error('Error fetching KiCad URL:', errorMessage);
    throw new Error(errorMessage);
  }
}